import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../environments/environment";

export interface Supplier {
  id: string;
  name: string;
  email: string;
}

export interface SupplierEstimateSummary {
  kind: "job" | "diy";
  customerId: string;
  orderId?: string;
  name: string;
  status: string;
  supplierQuoteStatus: string;
  hasQuote: boolean;
  trackingNumber?: string;
  updatedAt: string;
}

export interface EstimateItem {
  roomName: string;
  windowLabel: string;
  quantity: number;
  rate: number;
  discount: number;
  amount: number;
  style?: string;
  type?: "semi-blackout" | "blackout";
  controlType?: "manual" | "cordless" | "motorized";
  hasSolarPanel?: boolean;
}

export interface Estimate {
  id: string;
  customerId: string;
  items: EstimateItem[];
  subtotal: number;
  totalDiscount: number;
  total: number;
  createdAt: string;
  updatedAt: string;
}

export interface PresignResult {
  uploadUrl: string;
  objectKey: string;
  publicUrl: string;
}

export interface SupplierDiyOrderSummary {
  orderId: string;
  orderNumber?: string;
  createdAt: string;
  status: string;
  windowCount: number;
  remoteCount: number;
  quoteStatus: string;
  hasQuote: boolean;
  total: number;
}

export interface SupplierDiyOrderItem {
  label?: string;
  roomName?: string;
  blindName?: string;
  blindCode?: string;
  controlType?: string;
  opacity?: string;
  width?: number;
  height?: number;
  unit?: string;
  hasSolarPanel?: boolean;
  isRemote?: boolean;
}

export interface SupplierDiyOrderDetail {
  orderId: string;
  orderNumber?: string;
  createdAt: string;
  status: string;
  customerName?: string;
  customerEmail?: string;
  shippingAddress?: {
    name?: string;
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  };
  items: SupplierDiyOrderItem[];
  quoteStatus: string;
  quote?: { id: string; url: string; name: string; amount?: number; uploadedAt: string };
  alibabaUrl?: string;
  trackingNumber?: string;
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private readonly apiBase = this.resolveApiBase();

  constructor(private readonly http: HttpClient) {}

  private resolveApiBase(): string {
    if (typeof window === "undefined") {
      if (
        typeof environment.apiBaseUrl === "string" &&
        environment.apiBaseUrl.trim().length > 0
      ) {
        return environment.apiBaseUrl.replace(/\/+$/, "");
      }
      return "";
    }

    const globalApiBase = (window as unknown as { __API_BASE_URL__?: string })
      .__API_BASE_URL__;
    if (typeof globalApiBase === "string" && globalApiBase.trim().length > 0) {
      return globalApiBase.replace(/\/+$/, "");
    }

    const hostname = window.location?.hostname ?? "";
    const protocol = window.location?.protocol ?? "";
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      protocol === "file:"
    ) {
      return "http://localhost:4000";
    }

    if (
      typeof environment.apiBaseUrl === "string" &&
      environment.apiBaseUrl.trim().length > 0
    ) {
      return environment.apiBaseUrl.replace(/\/+$/, "");
    }

    const meta = document.querySelector('meta[name="api-base"]');
    const metaValue = meta?.getAttribute("content");
    if (typeof metaValue === "string" && metaValue.trim().length > 0) {
      return metaValue.replace(/\/+$/, "");
    }

    return "";
  }

  /** GET /suppliers/me using an explicit token (fresh Cognito idToken). */
  async meWithToken(idToken: string): Promise<Supplier> {
    return firstValueFrom(
      this.http.get<Supplier>(`${this.apiBase}/suppliers/me`, {
        headers: { Authorization: `Bearer ${idToken}` },
      }),
    );
  }

  me() {
    return this.http.get<Supplier>(`${this.apiBase}/suppliers/me`);
  }

  myEstimates() {
    return this.http.get<SupplierEstimateSummary[]>(
      `${this.apiBase}/suppliers/me/estimates`,
    );
  }

  estimateDetail(customerId: string) {
    return this.http.get<SupplierDiyOrderDetail>(
      `${this.apiBase}/suppliers/me/estimates/${customerId}`,
    );
  }

  presignQuote(customerId: string) {
    return this.http.post<PresignResult>(
      `${this.apiBase}/suppliers/me/estimates/${customerId}/quote/presign`,
      {},
    );
  }

  submitQuote(customerId: string, payload: { url: string; name: string; amount: number }) {
    return this.http.post<{ ok: boolean }>(
      `${this.apiBase}/suppliers/me/estimates/${customerId}/quote`,
      payload,
    );
  }

  setShipping(customerId: string, trackingNumber: string) {
    return this.http.post<{ ok: boolean }>(
      `${this.apiBase}/suppliers/me/estimates/${customerId}/shipping`,
      { trackingNumber },
    );
  }

  setJobAlibaba(customerId: string, url: string) {
    return this.http.post<{ ok: boolean }>(
      `${this.apiBase}/suppliers/me/estimates/${customerId}/alibaba`,
      { url },
    );
  }

  // ── DIY orders (storefront orders routed to this supplier) ────────────────
  myDiyOrders() {
    return this.http.get<SupplierDiyOrderSummary[]>(
      `${this.apiBase}/suppliers/me/diy-orders`,
    );
  }

  diyOrder(orderId: string) {
    return this.http.get<SupplierDiyOrderDetail>(
      `${this.apiBase}/suppliers/me/diy-orders/${orderId}`,
    );
  }

  presignDiyQuote(orderId: string) {
    return this.http.post<PresignResult>(
      `${this.apiBase}/suppliers/me/diy-orders/${orderId}/quote/presign`,
      {},
    );
  }

  submitDiyQuote(
    orderId: string,
    payload: { url: string; name: string; amount: number },
  ) {
    return this.http.post<{ ok: boolean }>(
      `${this.apiBase}/suppliers/me/diy-orders/${orderId}/quote`,
      payload,
    );
  }

  setDiyShipping(orderId: string, trackingNumber: string) {
    return this.http.post<{ ok: boolean }>(
      `${this.apiBase}/suppliers/me/diy-orders/${orderId}/shipping`,
      { trackingNumber },
    );
  }

  setDiyAlibaba(orderId: string, url: string) {
    return this.http.post<{ ok: boolean }>(
      `${this.apiBase}/suppliers/me/diy-orders/${orderId}/alibaba`,
      { url },
    );
  }
}
